// Raw line -> normalized event.
//
// Design notes
//  * Field layouts change between game versions, so the only hard assumption is
//    the shape of the "advanced parameters" tail (positionX, positionY, uiMapId,
//    facing, level). Everything after it is the event suffix, everything before
//    it the prefix. The detected layout is cached per (event name, field count)
//    pair, so detection is paid once per shape instead of once per line.
//  * parse() returns a single reused object. Consumers must copy whatever they
//    keep; nothing may hold the returned reference. Millions of short-lived
//    event objects would otherwise dominate processing time.

import { splitFields, unquote, isNilLike } from './tokenizer'

const DAMAGE_SUFFIX_EVENTS = new Set([
    'SWING_DAMAGE',
    'SWING_DAMAGE_LANDED',
    'SPELL_DAMAGE',
    'SPELL_PERIODIC_DAMAGE',
    'SPELL_BUILDING_DAMAGE',
    'RANGE_DAMAGE',
    'DAMAGE_SPLIT',
    'DAMAGE_SHIELD',
])

const HEAL_SUFFIX_EVENTS = new Set([
    'SPELL_HEAL',
    'SPELL_PERIODIC_HEAL',
    'SPELL_BUILDING_HEAL',
])

const ENERGIZE_EVENTS = new Set([
    'SPELL_ENERGIZE',
    'SPELL_PERIODIC_ENERGIZE',
    'SPELL_DRAIN',
    'SPELL_PERIODIC_DRAIN',
    'SPELL_LEECH',
    'SPELL_PERIODIC_LEECH',
])

const MISS_EVENTS = new Set([
    'SWING_MISSED',
    'SPELL_MISSED',
    'SPELL_PERIODIC_MISSED',
    'RANGE_MISSED',
    'SPELL_BUILDING_MISSED',
    'DAMAGE_SHIELD_MISSED',
])

const AURA_EVENTS = new Set([
    'SPELL_AURA_APPLIED',
    'SPELL_AURA_REMOVED',
    'SPELL_AURA_APPLIED_DOSE',
    'SPELL_AURA_REMOVED_DOSE',
    'SPELL_AURA_REFRESH',
])

// Events carrying a second spell: the interrupted, dispelled or broken spell.
const EXTRA_SPELL_EVENTS = new Set([
    'SPELL_INTERRUPT',
    'SPELL_DISPEL',
    'SPELL_DISPEL_FAILED',
    'SPELL_STOLEN',
    'SPELL_AURA_BROKEN_SPELL',
])

const CAST_EVENTS = new Set([
    'SPELL_CAST_START',
    'SPELL_CAST_SUCCESS',
    'SPELL_CAST_FAILED',
    'SPELL_EMPOWER_START',
    'SPELL_EMPOWER_END',
])

const DEATH_EVENTS = new Set([
    'UNIT_DIED',
    'UNIT_DESTROYED',
    'UNIT_DISSIPATES',
    'SPELL_INSTAKILL',
])

const META_EVENTS = new Set([
    'COMBAT_LOG_VERSION',
    'ZONE_CHANGE',
    'MAP_CHANGE',
    'ENCOUNTER_START',
    'ENCOUNTER_END',
    'CHALLENGE_MODE_START',
    'CHALLENGE_MODE_END',
    'COMBATANT_INFO',
    'EMOTE',
    'WORLD_MARKER_PLACED',
    'WORLD_MARKER_REMOVED',
    'ARENA_MATCH_START',
    'ARENA_MATCH_END',
])

const SPELL_PREFIXED = /^(SPELL|RANGE)_/
const BASE_FIELDS = 9

function isDecimalField(value) {
    if (!value) return false
    const dot = value.indexOf('.')
    if (dot < 1) return false
    return Number.isFinite(Number(value))
}

function isIntegerField(value) {
    if (!value) return false
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index)
        if (code === 45 && index === 0) continue
        if (code < 48 || code > 57) return false
    }
    return true
}

// The advanced block always ends with positionX, positionY, uiMapId, facing and
// level/item level. Locating that quintet keeps the parser working when Blizzard
// inserts new stat fields in the middle of the block.
function findAdvancedEnd(fields, start) {
    const limit = fields.length - 5
    for (let index = start + 2; index <= limit; index += 1) {
        if (
            isDecimalField(fields[index])
            && isDecimalField(fields[index + 1])
            && isIntegerField(fields[index + 2])
            && isDecimalField(fields[index + 3])
            && isIntegerField(fields[index + 4])
        ) {
            return index + 5
        }
    }
    return -1
}

function hasAdvancedBlock(name) {
    return DAMAGE_SUFFIX_EVENTS.has(name)
        || HEAL_SUFFIX_EVENTS.has(name)
        || ENERGIZE_EVENTS.has(name)
        || name === 'SPELL_CAST_SUCCESS'
        || name === 'SPELL_EMPOWER_END'
        || name === 'ENVIRONMENTAL_DAMAGE'
}

function prefixLength(name) {
    if (name === 'ENVIRONMENTAL_DAMAGE') return 0
    if (name.startsWith('SWING_')) return 0
    if (name === 'SPELL_ABSORBED' || name === 'SPELL_HEAL_ABSORBED') return 0
    if (SPELL_PREFIXED.test(name)) return 3
    return 0
}

function categoryOf(name) {
    if (DAMAGE_SUFFIX_EVENTS.has(name) || name === 'ENVIRONMENTAL_DAMAGE') return 'damage'
    if (HEAL_SUFFIX_EVENTS.has(name)) return 'heal'
    if (name === 'SPELL_ABSORBED') return 'absorbed'
    if (name === 'SPELL_HEAL_ABSORBED') return 'healAbsorbed'
    if (MISS_EVENTS.has(name)) return 'miss'
    if (ENERGIZE_EVENTS.has(name)) return 'energize'
    if (AURA_EVENTS.has(name) || name === 'SPELL_AURA_BROKEN' || name === 'SPELL_AURA_BROKEN_SPELL') return 'aura'
    if (CAST_EVENTS.has(name)) return 'cast'
    if (DEATH_EVENTS.has(name)) return 'death'
    if (name === 'PARTY_KILL') return 'kill'
    if (name === 'SPELL_SUMMON' || name === 'SPELL_CREATE') return 'summon'
    if (name === 'SPELL_RESURRECT') return 'resurrect'
    if (EXTRA_SPELL_EVENTS.has(name)) return 'utility'
    if (META_EVENTS.has(name)) return 'meta'
    return 'other'
}

function suffixKindOf(name) {
    if (name === 'ENVIRONMENTAL_DAMAGE') return 'environmental'
    if (DAMAGE_SUFFIX_EVENTS.has(name)) return 'damage'
    if (HEAL_SUFFIX_EVENTS.has(name)) return 'heal'
    if (MISS_EVENTS.has(name)) return 'miss'
    if (ENERGIZE_EVENTS.has(name)) return 'energize'
    if (AURA_EVENTS.has(name)) return 'aura'
    if (EXTRA_SPELL_EVENTS.has(name)) return 'extraSpell'
    if (name === 'SPELL_CAST_FAILED') return 'castFailed'
    if (name === 'SPELL_ABSORBED') return 'absorbed'
    if (name === 'SPELL_HEAL_ABSORBED') return 'healAbsorbed'
    if (name === 'SPELL_AURA_BROKEN') return 'auraBroken'
    if (name === 'SPELL_EXTRA_ATTACKS') return 'extraAttacks'
    return 'none'
}

// The client writes 'nil' for an absent unit name; the UI should never show it.
function readName(value) {
    return !value || value === 'nil' ? '' : unquote(value)
}

export function createLayoutResolver() {
    const cache = new Map()

    return function resolveLayout(name, fields) {
        const key = name + ':' + fields.length
        const cached = cache.get(key)
        if (cached) return cached

        const prefixLen = prefixLength(name)
        let advancedStart = -1
        let advancedEnd = -1

        if (hasAdvancedBlock(name)) {
            advancedStart = BASE_FIELDS + prefixLen
            advancedEnd = findAdvancedEnd(fields, advancedStart)
            if (advancedEnd < 0) {
                // The positional quintet is missing (older client, or a unit
                // without a position). Fall back to the current 19 field block
                // when that still leaves a plausible suffix, otherwise treat the
                // event as carrying no advanced data at all.
                const assumed = advancedStart + 19
                advancedEnd = assumed < fields.length ? assumed : -1
                if (advancedEnd < 0) advancedStart = -1
            }
        }

        const suffixStart = advancedEnd > 0 ? advancedEnd : BASE_FIELDS + prefixLen
        const layout = {
            name,
            prefixLen,
            advancedStart,
            advancedEnd,
            advancedLength: advancedEnd > 0 ? advancedEnd - advancedStart : 0,
            suffixStart,
            suffixKind: suffixKindOf(name),
            category: categoryOf(name),
        }

        cache.set(key, layout)
        return layout
    }
}

const DAY_MS_CACHE = new Map()

function dayStartMs(year, month, day) {
    const key = year * 10000 + month * 100 + day
    let value = DAY_MS_CACHE.get(key)
    if (value === undefined) {
        value = new Date(year, month - 1, day, 0, 0, 0, 0).getTime()
        if (DAY_MS_CACHE.size > 64) DAY_MS_CACHE.clear()
        DAY_MS_CACHE.set(key, value)
    }
    return value
}

// Accepts both the current "3/12/2026 19:34:27.8122" stamp and the older
// year-less "3/12 19:34:27.812" one. Month/day order depends on the client
// locale and is resolved once per file, see resolveDateOrder().
export function parseTimestamp(stamp, dateOrder) {
    const spaceIndex = stamp.indexOf(' ')
    if (spaceIndex < 0) return 0

    const datePart = stamp.slice(0, spaceIndex)
    const timePart = stamp.slice(spaceIndex + 1)

    const dateBits = datePart.split('/')
    if (dateBits.length < 2) return 0

    const first = Number(dateBits[0])
    const second = Number(dateBits[1])
    const year = dateBits.length > 2 ? Number(dateBits[2]) : new Date().getFullYear()
    const month = dateOrder === 'DMY' ? second : first
    const day = dateOrder === 'DMY' ? first : second
    if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) return 0

    const colon = timePart.indexOf(':')
    const secondColon = timePart.indexOf(':', colon + 1)
    if (colon < 0 || secondColon < 0) return 0

    const hours = Number(timePart.slice(0, colon))
    const minutes = Number(timePart.slice(colon + 1, secondColon))
    const seconds = Number(timePart.slice(secondColon + 1))
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) return 0

    return dayStartMs(year, month, day)
        + hours * 3600000
        + minutes * 60000
        + Math.round(seconds * 1000)
}

// Picks the month/day order from the first timestamp of the file and, when the
// date itself is ambiguous, from the MMDDYY stamp in the file name.
export function resolveDateOrder(stamp, fileNameHint) {
    const spaceIndex = stamp.indexOf(' ')
    if (spaceIndex < 0) return 'MDY'
    const bits = stamp.slice(0, spaceIndex).split('/')
    const first = Number(bits[0])
    const second = Number(bits[1])

    if (first > 12) return 'DMY'
    if (second > 12) return 'MDY'

    if (fileNameHint && fileNameHint.month && fileNameHint.day) {
        if (first === fileNameHint.month && second === fileNameHint.day) return 'MDY'
        if (first === fileNameHint.day && second === fileNameHint.month) return 'DMY'
    }

    return 'MDY'
}

// WoWCombatLog-031226_193427.txt -> { month: 3, day: 12, year: 2026, ... }
export function parseLogFileName(fileName) {
    const match = /WoWCombatLog-(\d{2})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/.exec(fileName || '')
    if (!match) return null
    const month = Number(match[1])
    const day = Number(match[2])
    const year = 2000 + Number(match[3])
    const hour = Number(match[4])
    const minute = Number(match[5])
    const second = Number(match[6])

    return {
        month,
        day,
        year,
        hour,
        minute,
        second,
        startedAt: new Date(year, month - 1, day, hour, minute, second).getTime(),
    }
}

function createEventShell() {
    return {
        ts: 0,
        name: '',
        category: '',
        sourceGuid: '',
        sourceName: '',
        sourceFlags: 0,
        destGuid: '',
        destName: '',
        destFlags: 0,
        spellId: 0,
        spellName: '',
        spellSchool: 0,
        amount: 0,
        baseAmount: 0,
        overkill: 0,
        overheal: 0,
        absorbed: 0,
        blocked: 0,
        resisted: 0,
        critical: false,
        school: 0,
        missType: '',
        amountMissed: 0,
        auraType: '',
        stacks: 0,
        extraSpellId: 0,
        extraSpellName: '',
        extraSpellSchool: 0,
        absorbCasterGuid: '',
        absorbCasterName: '',
        healerGuid: '',
        healerName: '',
        powerType: 0,
        overEnergize: 0,
        environmentalType: '',
        failedType: '',
        infoGuid: '',
        ownerGuid: '',
        currentHp: 0,
        maxHp: 0,
        attackPower: 0,
        spellPower: 0,
        armor: 0,
        absorbShield: 0,
        positionX: 0,
        positionY: 0,
        uiMapId: 0,
        level: 0,
        hasAdvanced: false,
        fields: null,
        layout: null,
    }
}

export function createParser(options = {}) {
    const resolveLayout = createLayoutResolver()
    const event = createEventShell()
    const fileNameHint = options.fileNameHint || null
    let dateOrder = options.dateOrder || ''

    function readAdvanced(fields, layout) {
        const start = layout.advancedStart
        const end = layout.advancedEnd
        event.hasAdvanced = true
        event.infoGuid = fields[start]
        event.ownerGuid = fields[start + 1]
        event.currentHp = +fields[start + 2] || 0
        event.maxHp = +fields[start + 3] || 0
        event.attackPower = +fields[start + 4] || 0
        event.spellPower = +fields[start + 5] || 0
        event.armor = +fields[start + 6] || 0
        event.absorbShield = +fields[start + 7] || 0
        event.positionX = +fields[end - 5] || 0
        event.positionY = +fields[end - 4] || 0
        event.uiMapId = +fields[end - 3] || 0
        event.level = +fields[end - 1] || 0
    }

    // 10+ fields: amount, baseAmount, overkill, school, resisted, blocked,
    //   absorbed, critical, glancing, crushing [, ST/AOE tag]
    // 9 fields (pre 11.0): amount, overkill, school, resisted, blocked,
    //   absorbed, critical, glancing, isOffHand
    function readDamageSuffix(fields, start, count) {
        if (count >= 10) {
            event.amount = +fields[start] || 0
            event.baseAmount = +fields[start + 1] || 0
            event.overkill = +fields[start + 2] || 0
            event.school = +fields[start + 3] || 0
            event.resisted = +fields[start + 4] || 0
            event.blocked = +fields[start + 5] || 0
            event.absorbed = +fields[start + 6] || 0
            event.critical = fields[start + 7] === '1'
            return
        }

        event.amount = +fields[start] || 0
        event.baseAmount = 0
        event.overkill = +fields[start + 1] || 0
        event.school = +fields[start + 2] || 0
        event.resisted = +fields[start + 3] || 0
        event.blocked = +fields[start + 4] || 0
        event.absorbed = +fields[start + 5] || 0
        event.critical = fields[start + 6] === '1'
    }

    function reset(fields, layout) {
        event.spellId = 0
        event.spellName = ''
        event.spellSchool = 0
        event.amount = 0
        event.baseAmount = 0
        event.overkill = 0
        event.overheal = 0
        event.absorbed = 0
        event.blocked = 0
        event.resisted = 0
        event.critical = false
        event.school = 0
        event.missType = ''
        event.amountMissed = 0
        event.auraType = ''
        event.stacks = 0
        event.extraSpellId = 0
        event.extraSpellName = ''
        event.extraSpellSchool = 0
        event.absorbCasterGuid = ''
        event.absorbCasterName = ''
        event.healerGuid = ''
        event.healerName = ''
        event.powerType = 0
        event.overEnergize = 0
        event.environmentalType = ''
        event.failedType = ''
        event.infoGuid = ''
        event.ownerGuid = ''
        event.currentHp = 0
        event.maxHp = 0
        event.attackPower = 0
        event.spellPower = 0
        event.armor = 0
        event.absorbShield = 0
        event.positionX = 0
        event.positionY = 0
        event.uiMapId = 0
        event.level = 0
        event.hasAdvanced = false
        event.fields = fields
        event.layout = layout
    }

    return {
        get dateOrder() {
            return dateOrder
        },
        setDateOrder(value) {
            dateOrder = value
        },
        // Returns the shared event object, or null when the line is not usable.
        // The result is only valid until the next parse() call.
        parse(line) {
            if (!line) return null
            const separator = line.indexOf('  ')
            if (separator < 1) return null

            const stamp = line.slice(0, separator)
            if (!dateOrder) dateOrder = resolveDateOrder(stamp, fileNameHint)
            const ts = parseTimestamp(stamp, dateOrder)
            if (!ts) return null

            const body = line.charCodeAt(line.length - 1) === 13
                ? line.slice(separator + 2, -1)
                : line.slice(separator + 2)
            if (!body) return null

            const fields = splitFields(body)
            const name = fields[0]
            if (!name) return null

            const layout = resolveLayout(name, fields)
            reset(fields, layout)
            event.ts = ts
            event.name = name
            event.category = layout.category

            if (layout.category === 'meta') {
                event.sourceGuid = ''
                event.sourceName = ''
                event.sourceFlags = 0
                event.destGuid = ''
                event.destName = ''
                event.destFlags = 0
                return event
            }

            event.sourceGuid = fields[1] || ''
            event.sourceName = readName(fields[2])
            event.sourceFlags = fields[3] ? parseInt(fields[3], 16) || 0 : 0
            event.destGuid = fields[5] || ''
            event.destName = readName(fields[6])
            event.destFlags = fields[7] ? parseInt(fields[7], 16) || 0 : 0

            if (layout.prefixLen === 3) {
                event.spellId = +fields[9] || 0
                event.spellName = unquote(fields[10])
                event.spellSchool = fields[11] ? parseInt(fields[11], 16) || 0 : 0
            } else if (name.charCodeAt(0) === 83 && name.startsWith('SWING_')) {
                // Melee carries no spell payload. Spell id 1 ("Melee") is the
                // convention shared with the rest of the WoW tooling.
                event.spellId = 1
                event.spellName = 'Melee'
                event.spellSchool = 1
            }

            if (layout.advancedEnd > 0) readAdvanced(fields, layout)

            const suffixStart = layout.suffixStart
            const suffixLength = fields.length - suffixStart

            switch (layout.suffixKind) {
                case 'damage':
                    readDamageSuffix(fields, suffixStart, suffixLength)
                    break
                case 'environmental':
                    event.environmentalType = unquote(fields[suffixStart])
                    readDamageSuffix(fields, suffixStart + 1, suffixLength - 1)
                    break
                case 'heal':
                    event.amount = +fields[suffixStart] || 0
                    if (suffixLength >= 5) {
                        event.baseAmount = +fields[suffixStart + 1] || 0
                        event.overheal = +fields[suffixStart + 2] || 0
                        event.absorbed = +fields[suffixStart + 3] || 0
                        event.critical = fields[suffixStart + 4] === '1'
                    } else {
                        event.overheal = +fields[suffixStart + 1] || 0
                        event.absorbed = +fields[suffixStart + 2] || 0
                        event.critical = fields[suffixStart + 3] === '1'
                    }
                    break
                case 'miss':
                    event.missType = unquote(fields[suffixStart])
                    if (suffixLength >= 3 && isIntegerField(fields[suffixStart + 2])) {
                        event.amountMissed = +fields[suffixStart + 2] || 0
                    }
                    break
                case 'energize':
                    event.amount = +fields[suffixStart] || 0
                    event.overEnergize = +fields[suffixStart + 1] || 0
                    event.powerType = +fields[suffixStart + 2] || 0
                    break
                case 'aura':
                    event.auraType = unquote(fields[suffixStart])
                    if (suffixLength >= 2) event.stacks = +fields[suffixStart + 1] || 0
                    break
                case 'extraSpell':
                    event.extraSpellId = +fields[suffixStart] || 0
                    event.extraSpellName = unquote(fields[suffixStart + 1])
                    event.extraSpellSchool = +fields[suffixStart + 2] || 0
                    if (suffixLength >= 4) event.auraType = unquote(fields[suffixStart + 3])
                    break
                case 'auraBroken':
                    event.auraType = unquote(fields[suffixStart])
                    break
                case 'castFailed':
                    event.failedType = unquote(fields[suffixStart])
                    break
                case 'absorbed': {
                    // Two shapes: the absorbed hit is either a spell (spell id in
                    // the prefix slot) or a melee swing (no spell payload).
                    const spellForm = isIntegerField(fields[9])
                    const casterStart = spellForm ? 12 : 9
                    event.spellId = spellForm ? +fields[9] || 0 : 1
                    event.spellName = spellForm ? unquote(fields[10]) : 'Melee'
                    event.spellSchool = spellForm ? parseInt(fields[11], 16) || 0 : 1
                    event.absorbCasterGuid = fields[casterStart] || ''
                    event.absorbCasterName = readName(fields[casterStart + 1])
                    event.extraSpellId = +fields[casterStart + 4] || 0
                    event.extraSpellName = unquote(fields[casterStart + 5])
                    event.extraSpellSchool = parseInt(fields[casterStart + 6], 16) || 0
                    event.amount = +fields[casterStart + 7] || 0
                    event.baseAmount = +fields[casterStart + 8] || 0
                    break
                }
                case 'healAbsorbed':
                    // source/dest are the absorb caster and the unit whose heal
                    // was absorbed; the healer and the heal spell follow.
                    event.extraSpellId = +fields[16] || 0
                    event.extraSpellName = unquote(fields[17])
                    event.healerGuid = fields[12] || ''
                    event.healerName = readName(fields[13])
                    event.amount = +fields[19] || 0
                    event.baseAmount = +fields[20] || 0
                    break
                case 'extraAttacks':
                    event.amount = +fields[suffixStart] || 0
                    break
                default:
                    break
            }

            return event
        },
    }
}

export { isNilLike }
